import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BookOpen, Filter } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { EBookCard } from '../components/common/EBookCard';
import { API_BASE } from '../config/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${API_BASE}/api`;

const CATEGORIES = [
  'All Categories',
  'Micro-learning Unit',
  'Short Course',
  'Specialist Courses',
  'Compliance & Regulatory',
  'Leadership & Management',
  'Project Management',
  'Ethics & Governance'
];

export default function EBooksPage() {
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('');

  useEffect(() => {
    const fetchEbooks = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (category && category !== 'All Categories') {
          params.append('category', category);
        }
        const res = await axios.get(`${API}/ebooks?${params.toString()}`);
        setEbooks(res.data || []);
      } catch (error) {
        console.error('Error fetching ebooks:', error);
        setEbooks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEbooks();
  }, [category]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold text-white mb-2">
            eBook Library
          </h1>
          <p className="text-zinc-400">
            Essential reading materials for professional development
          </p>
        </div>

        {/* Filter */}
        <div className="mb-8 flex items-center gap-4">
          <Filter className="h-5 w-5 text-zinc-400" />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[200px] bg-navigate-surface border-zinc-800">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="bg-navigate-surface border-zinc-800">
              {CATEGORIES.map((cat) => (
                <SelectItem 
                  key={cat} 
                  value={cat}
                  className="hover:bg-zinc-800"
                >
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <span className="text-sm text-zinc-500">
            {ebooks.length} eBook{ebooks.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* eBooks Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden animate-pulse">
                <div className="aspect-[3/4] bg-zinc-800" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-zinc-800 rounded w-2/3" />
                  <div className="h-3 bg-zinc-800 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : ebooks.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">No eBooks found</h3>
            <p className="text-zinc-400">
              {category ? 'Try selecting a different category' : 'Check back soon for new eBooks'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {ebooks.map((ebook) => (
              <EBookCard key={ebook.ebook_id} ebook={ebook} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
